import { cn } from "@/lib/utils";
import { usePublishFormTemplate } from "@/hooks/template";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  formId: string;
}

interface FormValues {
  description: string;
  category: string;
}

export const PublishFormTemplateModal: React.FC<Props> = ({
  isOpen,
  onClose,
  formId,
}) => {
  const { t } = useTranslation();
  const publishMutation = usePublishFormTemplate();

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: { description: "", category: "" },
  });

  const onSubmit = async (data: FormValues) => {
    await publishMutation.mutateAsync({
      formId,
      description: data.description || undefined,
      category: data.category || undefined,
    });
    reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:rounded">
        <DialogHeader>
          <DialogTitle>{t("templates.publishFormModal.title")}</DialogTitle>
          <DialogDescription>
            {t("templates.publishFormModal.description")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-4">
            <label className="main-label">
              {t("templates.publishFormModal.descriptionLabel")}
            </label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <textarea
                  {...field}
                  rows={3}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-darkBlue focus:border-darkBlue sm:text-sm"
                />
              )}
            />
          </div>

          <div className="mb-6">
            <label className="main-label">
              {t("templates.publishFormModal.categoryLabel")}
            </label>
            <Controller
              name="category"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-darkBlue focus:border-darkBlue sm:text-sm"
                />
              )}
            />
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={publishMutation.isPending}
              className={cn(
                "main-dark-button",
                publishMutation.isPending && "opacity-50 cursor-not-allowed",
              )}
            >
              {publishMutation.isPending
                ? "Publishing..."
                : t("templates.publishFormModal.confirm")}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
